import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Cell, Label, Pie, PieChart, Tooltip } from 'recharts';

import { getAllUniqueTaskIdsFromReport } from '../../../../../../js/helper/dashboardsHelpers';
import { useContainerSize } from '../../../../../../js/useContainerSize';
import {
  CHART_COLORS,
  CHART_TYPES,
  DEFAULT_RECHARTS_COLORS,
} from '../constants';
import CustomTooltip from '../CustomTooltip';
import EmptyState from '../Empty/EmptyState';
import LoadingState from '../Loadinng/LoadingState';
import { formatLabel } from '../utils';

const RADIAN = Math.PI / 180;
const MIN_PERCENT_TO_SHOW_LABEL = 0.08;

const LEGEND_WIDTH = 200;
const LEGEND_GAP = 16;
const DEFAULT_OUTER_RADIUS = 180;
const LEGEND_STACK_BREAKPOINT = 420;
const LEGEND_STACK_MIN_HEIGHT = 72;
const OUTER_RADIUS_FLOOR = 56;

const MIN_SLICE_LABEL_FONT_SIZE = 10;
const MAX_SLICE_LABEL_FONT_SIZE = 16;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  value,
  percent,
}) => {
  if (
    cx == null ||
    cy == null ||
    innerRadius == null ||
    outerRadius == null ||
    percent > 0.99 ||
    percent < MIN_PERCENT_TO_SHOW_LABEL
  ) {
    return null;
  }

  const ringThickness = outerRadius - innerRadius;
  // Scale with the ring's actual thickness (the center total label already
  // does this — see `<Label fontSize={Math.max(24, outerRadius * 0.4)}>`
  // below). A fixed size here is what let labels overflow a thin ring on a
  // narrow card. Below the legible floor, skip the label entirely — the
  // legend repeats the same name + value, so nothing is lost.
  const fontSize = Math.min(
    MAX_SLICE_LABEL_FONT_SIZE,
    Math.floor(ringThickness * 0.45),
  );
  if (fontSize < MIN_SLICE_LABEL_FONT_SIZE) {
    return null;
  }

  const radius = innerRadius + ringThickness * 0.5;
  const x = Number(cx) + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
  const y = Number(cy) + radius * Math.sin(-(midAngle ?? 0) * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={fontSize}
      fontWeight="600"
    >
      {value}
    </text>
  );
};

const PieGraph = ({
  chartData,
  colors = DEFAULT_RECHARTS_COLORS,
  isLoading,
  uniqueTaskCount,
  xLabelType,
  xLabelEntities,
  handleSliceClick = () => {},
}) => {
  const { ref, size: containerSize } = useContainerSize();
  const wrapperRef = useRef(null);
  const [tooltipY, setTooltipY] = useState(undefined);

  const handleMouseMove = useCallback((e) => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipY(e.clientY - rect.top + 15);
    }
  }, []);

  const handleMouseLeave = useCallback(() => setTooltipY(undefined), []);

  // The centre label and every legend percentage are the DE-DUPLICATED task
  // total, not sum(value) — one task can sit in several slices (assignee, tags,
  // multi-select custom fields). The server now ships that number; deriving it
  // from per-slice taskIds is the fallback for responses that still carry them
  // (and for anything cached by an older build).
  const total = useMemo(
    () =>
      Number.isFinite(uniqueTaskCount)
        ? uniqueTaskCount
        : getAllUniqueTaskIdsFromReport(chartData)?.length,
    [chartData, uniqueTaskCount],
  );

  const dataWithTotal = useMemo(
    () => chartData.map((item) => ({ ...item, total })),
    [chartData, total],
  );

  // Below the breakpoint the legend moves under the chart (see JSX below),
  // so the pie no longer has to share its row with a fixed-width legend —
  // that fixed 200px reservation was what forced the ring down to its floor
  // on a typical laptop-width card.
  const isLegendStacked =
    containerSize.width > 0 && containerSize.width < LEGEND_STACK_BREAKPOINT;

  const { outerRadius, innerRadius } = useMemo(() => {
    if (!containerSize.width || !containerSize.height) {
      return {
        outerRadius: DEFAULT_OUTER_RADIUS,
        innerRadius: DEFAULT_OUTER_RADIUS * 0.58,
      };
    }

    const maxFromWidth = isLegendStacked
      ? containerSize.width / 2 - 20
      : (containerSize.width - LEGEND_WIDTH - LEGEND_GAP) / 2 - 20;
    const maxFromHeight = isLegendStacked
      ? (containerSize.height - LEGEND_STACK_MIN_HEIGHT) / 2 - 20
      : containerSize.height / 2 - 20;
    const outer = Math.max(
      OUTER_RADIUS_FLOOR,
      Math.min(maxFromWidth, maxFromHeight, DEFAULT_OUTER_RADIUS),
    );
    return { outerRadius: outer, innerRadius: outer * 0.58 };
  }, [containerSize, isLegendStacked]);

  const pieConfig = useMemo(
    () => ({
      innerRadius,
      outerRadius,
      paddingAngle: chartData.length <= 1 ? 0 : chartData.length > 8 ? 1 : 2,
      startAngle: 90,
      endAngle: -270,
    }),
    [innerRadius, outerRadius, chartData.length],
  );

  const pieSize = useMemo(() => (outerRadius + 20) * 2, [outerRadius]);

  if (isLoading) {
    return <LoadingState type={CHART_TYPES.PIE} />;
  }

  if (!chartData.length) {
    return <EmptyState type={CHART_TYPES.PIE} />;
  }

  if (!containerSize.width || !containerSize.height) {
    return <div ref={ref} className="w-full h-full" />;
  }

  return (
    <div ref={wrapperRef} style={{ width: '100%', height: '100%' }} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
    <div
      ref={ref}
      className={`flex items-center justify-center w-full h-full ${isLegendStacked ? 'flex-col' : 'flex-row'}`}
    >
      <div className="shrink-0">
        <PieChart
          width={pieSize}
          height={pieSize}
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          style={{ padding: 0 }}
        >
          <Pie
            data={dataWithTotal}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            {...pieConfig}
            label={chartData.length > 1 ? renderCustomizedLabel : false}
            labelLine={false}
            onClick={(sliceData) => handleSliceClick(sliceData, chartData)}
          >
            {dataWithTotal.map((entry, index) => (
              <Cell
                style={{ cursor: 'pointer' }}
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
            <Label
              value={total}
              position="center"
              fontSize={Math.max(24, outerRadius * 0.4)}
              fontWeight={600}
              fill={CHART_COLORS.DARK_LABELS}
            />
          </Pie>
          <Tooltip
            content={
              <CustomTooltip
                xLabelEntities={xLabelEntities}
                xLabelType={xLabelType}
                isPie={true}
              />
            }
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={{ zIndex: 9999 }}
            position={tooltipY !== undefined ? { y: tooltipY } : undefined}
          />
        </PieChart>
      </div>

      <div
        className={
          isLegendStacked
            ? 'shrink-0 flex flex-col gap-2 pt-2 text-xs w-full max-h-[35%] overflow-y-auto'
            : 'shrink-0 flex flex-col gap-2 pl-4 text-xs max-w-[200px] max-h-full overflow-y-auto self-stretch'
        }
      >
        <div
          className={
            isLegendStacked
              ? 'flex flex-wrap gap-x-3 gap-y-1.5 justify-center'
              : 'my-auto flex flex-col gap-1.5'
          }
        >
          {chartData.map((entry, index) => {
            const percent = total > 0 ? entry.value / total : 0;
            const isSmall = percent < MIN_PERCENT_TO_SHOW_LABEL && chartData.length > 1;
            return (
              <div
                key={entry.name}
                className={
                  isLegendStacked
                    ? 'inline-flex items-center gap-1.5 cursor-pointer group'
                    : 'flex items-center justify-between gap-2 cursor-pointer group'
                }
                onClick={() => handleSliceClick(entry, chartData)}
                title={isSmall ? `${formatLabel(entry.name, xLabelType, xLabelEntities)}: ${entry.value} (${(percent * 100).toFixed(1)}%)` : undefined}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <div
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ backgroundColor: colors[index % colors.length] }}
                  />
                  <span className="text-gray-700 leading-4 truncate group-hover:text-gray-900">
                    {formatLabel(entry.name, xLabelType, xLabelEntities)}
                    {isSmall && <span className="ml-1 text-gray-400">*</span>}
                  </span>
                </div>
                <span className="text-gray-400 font-semibold shrink-0 tabular-nums">
                  {entry.value}
                </span>
              </div>
            );
          })}
          {chartData.some((entry) => total > 0 && entry.value / total < MIN_PERCENT_TO_SHOW_LABEL && chartData.length > 1) && (
            <p className="text-[10px] text-gray-400 mt-1 w-full text-center">* too small to label on chart</p>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default PieGraph;
